import React, { useState, useEffect } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { X } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import callApi from "Common/utils/apiCaller";
import _ from "lodash";

const CategoryForm = () => {
  document.title = "Add Category | SuperSalesIQ";
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [categoryData, setCategoryData] = useState<any>(null);

  useEffect(() => {
    // Check if we're in edit mode (URL contains an ID)
    const searchParams = new URLSearchParams(location.search);
    const categoryId = searchParams.get("id");
    
    if (categoryId) {
      setIsEdit(true);
      fetchCategoryData(categoryId);
    }
  }, [location]);

  const fetchCategoryData = async (categoryId: string) => {
    setLoading(true);
    try {
      const res = await callApi(`v1/category/${categoryId}`, "get");
      if (res && res.data && res.data.data) {
        setCategoryData(res.data.data);
        validation.setValues({
          name: res.data.data.name || "",
          description: res.data.data.description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching category data:", error);
      toast("Failed to fetch category data");
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Category Name"),
      description: Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEdit) {
          // Update existing category
          const res = await callApi(`v1/category/${categoryData._id}/update`, "post", {
            category: {
              name: values.name,
              description: values.description,
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Category updated successfully");
            setTimeout(() => {
              navigate("/category");
            }, 1500);
          } else {
            toast("Failed to update category");
          }
        } else {
          // Create new category
           const res = await callApi(`v1/blog_category/new`, "post", {
                      blog_category: {
                        slug: _.kebabCase(values?.name.toLowerCase()),
                        name: values?.name,
                      },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Category created successfully");
            setTimeout(() => {
              navigate("/category");
            }, 1500);
          } else {
            toast("Failed to create category");
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb title={isEdit ? "Edit Category" : "Add Category"} pageTitle="Categories" />
      <ToastContainer closeButton={false} limit={1} />
      
      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 card 2xl:col-span-12">
          <div className="card-body">
            <div className="flex items-center">
              <h6 className="text-15 grow">{isEdit ? "Edit Category" : "Add Category"}</h6>
              <div className="shrink-0">
                <Link
                  to="/category"
                  className="text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:border-zink-600 dark:hover:border-zink-500 dark:text-zink-200 dark:ring-zink-400/20"
                >
                  <X className="inline-block size-4" />{" "}
                  <span className="align-middle">Cancel</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body">
            {loading && !validation.isSubmitting ? (
              <div className="flex items-center justify-center h-60">
                <TailSpin visible={true} height="40" width="40" color="#4fa94d" />
              </div>
            ) : (
              <form
                className="create-form"
                onSubmit={validation.handleSubmit}
              >
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="name"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Category Name*
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter category name"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.name}
                    />
                    {validation.touched.name &&
                    validation.errors.name ? (
                      <p className="text-red-500">
                        {validation.errors.name}
                      </p>
                    ) : null}
                  </div>
                  {/* <div className="xl:col-span-12">
                    <label
                      htmlFor="description"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter description"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.description}
                      rows={4}
                    />
                  </div> */}
                  <div className="xl:col-span-12">
                    <div className="flex justify-end gap-2 mt-4">
                      <Link
                        to="/category"
                        className="text-red-500 bg-red-100 btn hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:ring active:ring-red-100 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500 dark:hover:text-white dark:focus:bg-red-500 dark:focus:text-white dark:active:bg-red-500 dark:active:text-white dark:ring-red-400/20"
                      >
                        Cancel
                      </Link>
                      <button
                        type="submit"
                        disabled={validation.isSubmitting || loading}
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        {validation.isSubmitting || loading ? (
                          <TailSpin
                            visible={true}
                            height="20"
                            width="20"
                            color="#fff"
                            ariaLabel="tail-spin-loading"
                            radius="1"
                            wrapperStyle={{}}
                            wrapperClass=""
                          />
                        ) : isEdit ? (
                          "Update Category"
                        ) : (
                          "Add Category"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CategoryForm;
